import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import {
  CreateOutputConfigElement,
  Environments,
  UpdateOutputConfigElement
} from "@emisgroup/cohort-builder-kit";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { BandsTypes } from "../../Banding/Types";
import { Actions } from "../OutputMappingButtonsBar/Types";
import { OutputMappingAgeForm } from "../OutputMappingAgeForm/OutputMappingAgeForm.logic";
import { OutputMappingGenderForm } from "../OutputMappingGenderForm/OutputMappingGenderForm.logic";
import { OutputMappingEthnicityForm } from "../OutputMappingEthnicityForm/OutputMappingEthnicityForm.logic";
import { ProgressSpinnerSlice } from "../../../../ProgressSpinner/Index";
import { setNotification } from "../../../../../utilities/HttpRequest/Script";
import { ConfigDetailsForm } from "../ConfigDetailsForm/ConfigDetailsForm.logic";
import { NotificationType } from "../../../../Notification/redux/Types";
import { CONFIGSAVE } from "../../Constant";
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogInner,
  DialogHeader,
  DialogContent,
  DialogFooter
} from "../../../../Common/UI/Index";
import { ButtonVariantType } from "../../../../Common/UI/Types";
import { handleException } from "../../../../../ErrorMessages/helper";
import { useUser } from "../../../../../AuthContext";

interface Props {
  /**
   * Close Action
   */
  onClosePopup: (isReloadRequired: boolean) => void;
  /**
   * Flag to Show popup
   */
  isShow: boolean;
  /**
   * Popup action
   */
  action: string;
  /**
   * Selected band 
   */
  band: string;
}

/**
 * Responsible for rendering Output Mapping popup
 * @returns Output Mapping popup view
 */
export const OutputMappingPopupView: React.FC<Props> = (props: Props) => {
  const { onClosePopup, isShow, action, band } = props;
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const title = (action === Actions.CREATE || action === Actions.MODIFY) ?
    "OutputConfig.OutputMappingDialog.ConfigDetails" :
    (`OutputConfig.OutputMappingDialog.${band}Band`);
  const bandingComponents = {
    [BandsTypes.AGE]: <OutputMappingAgeForm />,
    [BandsTypes.GENDER]: <OutputMappingGenderForm />,
    [BandsTypes.ETHNICITY]: <OutputMappingEthnicityForm />
  };
  const isConfigDetailsForm = (action === Actions.CREATE || action === Actions.MODIFY);

  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const enableSave = outputConfig.enableMappingPopupSave;
  const bandMaps = outputConfig.outputMappingPopupBandData;
  const outputConfigId = outputConfig.selectedOutputConfig.id;
  const rootMap = outputConfig.outputMappingPopupConfigData;
  const editRootMap = outputConfig.outputMappingPopupEditConfigData;
  const tagName = outputConfig.selectedOutputMapping.configTag;
  const { user } = useUser();
  const env = process.env.APP_TARGET_ENV as Environments;

  /**
   * To get the dialog content based on banding type
   * @returns the banding component
   */
  const getForm = (): JSX.Element => {
    if (isConfigDetailsForm) {
      return <ConfigDetailsForm action={action} />;
    }
    return bandingComponents[band];
  };

  /**
   * To save band details from popup
   */
  const onSave = async (): Promise<void> => {
    const payload = {
      outputConfigMaps: bandMaps.map((bandMap) => ({
        name: bandMap.configName,
        tag:bandMap.tag,
        includeInOutput: bandMap.includeInOutput,
        parentOutputElementId: bandMap.parentMapId,
        bandId: bandMap.bandGuid
      })
      )
    };
    try {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.OutputConfigList")));
      const res = await CreateOutputConfigElement(payload, outputConfigId, env, user?.token);
      if (res) {
        onClosePopup(true);
        dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      }
    } catch (ex) {
      handleException(ex, dispatch, "SaveMapOutputDetailErrMsg");
    }
  };

  const onSaveConfig = async (): Promise<void> => {
    const payload = {
      outputConfigMaps: [{
        name: rootMap.configName,
        tag: rootMap.tag,
        sourceArtefact: rootMap?.sourceReport?.reportERN as string,
        includeInOutput: rootMap.includeInOutput
      }]
    };
    const editPayload = {
      name: editRootMap.configName,
      tag: editRootMap.tag,
      sourceArtefact: editRootMap.sourceReport?.reportERN as string,
      includeInOutput: editRootMap.includeInOutput
    };
    try {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.OutputConfigList")));
      let res;
      if (action === Actions.CREATE) {
        res = await CreateOutputConfigElement(payload, outputConfigId, env, user?.token);
      }
      else if (action === Actions.MODIFY) {
        res = await UpdateOutputConfigElement(outputConfigId, editPayload, editRootMap.configMapId, env, user?.token);
      }
      if (res) {
        onClosePopup(true);
        dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
        setNotification(dispatch, CONFIGSAVE, translate("Notification.SaveConfigMap"), NotificationType.Confirmation);
      }
    } catch (ex) {
      handleException(ex, dispatch, "EditMappingDetailErrMsg");
    }

  };

  /**
   * Call when close/cancel is done 
   */
  const onClose = (): void => {
    onClosePopup(false);
  };

  const nameOfTag = tagName ? `- ${tagName}` : "";

  return (
    <div id="ebt-outputConfig-config-configDetails-dialog">
      {isShow &&
        <Dialog
          className={isConfigDetailsForm ? "ebt-extract__output-config-dialog-small" : ""}
          open={isShow}
          onOpenChange={onClose}
          preventBackdropClose={true}
        >
          <DialogInner className={isConfigDetailsForm ? "ebt-extract__output-config-dialog-small-inner" : ""}>
            <DialogHeader>
              <div>
                {translate(title, { tag: nameOfTag })}
              </div>
            </DialogHeader>
            <DialogContent
              className="ebt-extract__output-config-dialog-content"
              id="ebt-outputConfig-config-configDetails-dialog-content"
            >
              <div>
                {getForm()}
              </div>
            </DialogContent>
            <DialogFooter id="ebt-outputConfig-config-configDetails-dialog-footer">
              <ButtonGroup>
                <Button
                  borderless={true}
                  onClick={onClose}
                  id="ebt-outputConfig-config-configDetails-cancelbtn"
                >
                  {translate("Button.Cancel")}
                </Button>
                <Button
                  onClick={isConfigDetailsForm ? onSaveConfig : onSave}
                  disabled={!enableSave}
                  variant={ButtonVariantType.FILLED}
                  id="ebt-outputConfig-config-configDetails-save-btn"
                >
                  {translate("Button.Save")}
                </Button>
              </ButtonGroup>
            </DialogFooter>
          </DialogInner>
        </Dialog>
      }
    </div>
  );
};
